// Destination picker for Move / manual put-away. Parent owns the mutation so
// this component stays reusable — it just fetches empty slots, filters by
// type compatibility, and calls onPick(code) on click.

import React, { useMemo } from 'react';
import { Modal, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';

import { fetchSlots } from '../../features/warehouse/warehouseApi.js';

export default function SlotPickerModal({
  show,
  onHide,
  onPick,
  buildingId,
  title = 'Pick a slot',
  excludeCode,
  allowedForType,
}) {
  const { data: slots, isLoading, error } = useQuery({
    queryKey: ['warehouse', 'slots', { building: buildingId, status: 'empty' }],
    queryFn: () => fetchSlots({ building: buildingId, status: 'empty' }),
    enabled: !!buildingId && show,
    staleTime: 30_000,
  });

  const grouped = useMemo(() => {
    if (!slots) return {};
    const out = {};
    for (const s of slots) {
      if (s.code === excludeCode) continue;
      if (allowedForType && !(s.allowedTypes || []).includes(allowedForType)) continue;
      if (!out[s.aisle]) out[s.aisle] = [];
      out[s.aisle].push(s);
    }
    for (const aisle of Object.keys(out)) {
      out[aisle].sort((a, b) => a.bay - b.bay || a.level - b.level);
    }
    return out;
  }, [slots, excludeCode, allowedForType]);

  const aisles = Object.keys(grouped).sort();

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {error && <Alert variant="danger">{error.message}</Alert>}
        {!isLoading && aisles.length === 0 && (
          <Alert variant="warning">
            No empty {allowedForType ? `${allowedForType}-compatible ` : ''}slots found.
          </Alert>
        )}
        {aisles.map(aisle => (
          <div key={aisle} className="mb-3">
            <div className="small fw-bold text-muted mb-1">Aisle {aisle}</div>
            <div className="d-flex flex-wrap gap-1">
              {grouped[aisle].map(slot => (
                <Badge
                  key={slot.code}
                  bg="light"
                  text="dark"
                  style={{
                    cursor: 'pointer',
                    border: '1px solid #adb5bd',
                    padding: '6px 10px',
                    fontSize: '0.8rem',
                  }}
                  onClick={() => {
                    onPick(slot.code);
                    onHide();
                  }}
                >
                  {slot.code}
                </Badge>
              ))}
            </div>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
